import React, { memo, useState, useEffect } from 'react';
import { useCart } from './CartContext';
import { ToastContainer, toast } from 'react-toastify';
import { Button } from 'react-bootstrap';
import ProductDetailsModal from './ProductDetailsModal';
import SquareCardForm from './SquareCardForm';
import countryData from './data/countries.json';
import zone2Rates from './data/usps-zone2.json';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import { API } from 'aws-amplify';

const API_NAME = 'apiNtrepid';

const ShoppingCart = memo(() => {
  const { cartItems, removeFromCart, clearCart } = useCart();
  const [shippingCost, setShippingCost] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [cardInstance, setCardInstance] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    email: '',
    subscribed: false
  });

  // ➜ helper to convert weight to pounds
  const toLbs = item => (item.uom === 'oz' ? item.weight / 16 : item.weight);

  // ➜ recalc totals whenever cart changes
  useEffect(() => {
    const subtotal = cartItems.reduce((t, i) => t + parseFloat(i.salePrice || i.price), 0);

    // total weight in pounds (rounded up)
    const totalLbs = cartItems.reduce((w, i) => w + toLbs(i), 0);
    const rounded  = Math.ceil(totalLbs);

    // pick first rate whose maxWeightLbs >= rounded, else last entry
    const bracket  = zone2Rates.find(r => rounded <= r.maxWeightLbs) || zone2Rates[zone2Rates.length - 1];
    const shipping = rounded === 0 ? 0 : Number(bracket?.rate) || 0;

    setShippingCost(shipping);
    setTotalAmount(subtotal + shipping);
  }, [cartItems]);

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const validateForm = () => {
    const required = ['firstName','lastName','streetAddress','city','state','zip','email'];
    for (const f of required) if (!formData[f]) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  };

  const handleCardPayment = async () => {
    if (!validateForm()) { toast.warn('Fill all required fields'); return; }
    if (!cardInstance) { toast.error('Payment form not ready'); return; }
    try {
      setIsProcessing(true);
      const tokenRes = await cardInstance.tokenize();
      if (tokenRes.status !== 'OK') throw new Error('Tokenization failed');

      const payload = {
        sourceId: tokenRes.token,
        idempotencyKey: crypto.randomUUID(),
        locationId: process.env.REACT_APP_SQUARE_LOCATION_ID,
        amountMoney: { amount: Math.round(totalAmount * 100), currency: 'USD' },
        customer: formData,
        items: cartItems
      };

      const res = await API.post(API_NAME, '/process-payment', { body: payload });
      toast.success('Order complete!');
      clearCart();
      window.location.href = res.payment?.receiptUrl || '/';
    } catch (err) {
      console.error('Order error:', err);
      toast.error('Order could not be completed');
    } finally { setIsProcessing(false); }
  };

  const subtotal = cartItems.reduce((t,i)=>t+parseFloat(i.salePrice||i.price),0);

  return (
    <div className="container mt-5">
      <ToastContainer />
      <h2>Shopping Cart</h2>
      {cartItems.length===0 ? <p>Your cart is empty.</p> : (
        <>
          <div className="row">
            <div className="col-md-6">
              <h4>Shipping Information</h4>
              {['firstName','lastName','streetAddress','city','state','zip','email'].map(f=> (
                <div className="form-group" key={f}>
                  <label>{f.charAt(0).toUpperCase()+f.slice(1).replace(/([A-Z])/g,' $1')}</label>
                  <input className="form-control" name={f} type={f==='email'?'email':'text'} value={formData[f]} onChange={handleInputChange}/>
                </div>))}
              <div className="form-group">
                <label>Country</label>
                <select className="form-control" name="country" value={formData.country} onChange={handleInputChange}>
                  {countryData.map(c=> <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-check mb-3">
                <input id="subChk" className="form-check-input" type="checkbox" name="subscribed" checked={formData.subscribed} onChange={handleInputChange}/>
                <label htmlFor="subChk" className="form-check-label">Subscribe to item updates</label>
              </div>
            </div>
            <div className="col-md-6">
              <h4>Your Items</h4>
              <ul className="list-group">
                {cartItems.map((it,i)=>(
                  <li key={i} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{it.name}</strong>
                      <p className="mb-0 text-muted">Size: {it.size||'N/A'}</p>
                      <p className="mb-0 text-muted">${parseFloat(it.salePrice||it.price).toFixed(2)}</p>
                      <p className="mb-0 text-muted">Weight: {it.weight} {it.uom}</p>
                    </div>
                    <Button variant="danger" size="sm" onClick={()=>removeFromCart(i)}>Remove</Button>
                  </li>))}
              </ul>
              <h5 className="mt-3">Subtotal: ${subtotal.toFixed(2)}<br/>Shipping: ${shippingCost.toFixed(2)}<br/><strong>Total: ${totalAmount.toFixed(2)}</strong></h5>
            </div>
          </div>
          <SquareCardForm onCardReady={setCardInstance} />
          <div className="row mt-4">
            <div className="col-md-12">
              <Button className="btn btn-primary" disabled={isProcessing} onClick={handleCardPayment}>
                {isProcessing ? 'Processing…' : 'Pay Now with Card'}
              </Button>
            </div>
          </div>
        </>) }
      <ProductDetailsModal show={showModal} handleClose={()=>setShowModal(false)} product={selectedProduct}/>
    </div>
  );
});

export default ShoppingCart;
